import { useState, useRef, useCallback } from 'react'
import {
	Dialog,
	Box,
	Typography,
	Button,
	IconButton,
	Alert,
	LinearProgress,
	useMediaQuery,
	useTheme
} from '@mui/material'
import {
	Close as CloseIcon,
	Download as DownloadIcon,
	UploadFile as UploadFileIcon,
	CheckCircle as CheckCircleIcon,
	Description as DescriptionIcon
} from '@mui/icons-material'
import { supabase } from '../utils/supabase'
import posthog, { isPostHogConfigured } from '../posthog'
import {
	parseClientsCsv,
	parsePetsCsv,
	buildClientInsertRows,
	buildPetInsertRows,
	countPetsPerOwner,
	MAX_ROWS_PER_FILE
} from '../utils/csvImport'

const CLIENT_CHUNK_SIZE = 500
const PET_CHUNK_SIZE = 500

// Nami lavender theme tokens (matches the client/pet pages)
const COLORS = {
	primaryBg: '#EDE9FB',
	primaryBorder: '#7C6BD9',
	primaryText: '#5F4EC2',
	textDark: '#2E2A40',
	textGray: '#8B85A4',
	white: '#FFFFFF',
	grayBorder: '#E7E2F3',
	grayBg: '#F5F3FB',
	error: '#BE4B45',
	errorBg: '#FBECEB',
	warning: '#8F6217',
	warningBg: '#FDF3E4',
	success: '#2F8A5B'
}
const FONT = "'Outfit', sans-serif"

function ImportClientsPetsDialog({ open, onClose, merchant, onImportComplete }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [step, setStep] = useState('upload') // 'upload' | 'preview' | 'importing' | 'result'

	const [clientsFile, setClientsFile] = useState(null)
	const [petsFile, setPetsFile] = useState(null)
	const [parseError, setParseError] = useState('')
	const [parsing, setParsing] = useState(false)

	// Parsed data
	const [clientsParsed, setClientsParsed] = useState(null)
	const [petsParsed, setPetsParsed] = useState(null)

	// Import progress
	const [progress, setProgress] = useState({ phase: '', current: 0, total: 0 })
	const [importResult, setImportResult] = useState(null)
	// importResult shape:
	// { ok: true, insertedClients, insertedPets }
	// { ok: false, phase: 'clients' | 'pets', message, insertedClientIds, insertedPetIds }

	const clientsInputRef = useRef(null)
	const petsInputRef = useRef(null)

	const reset = useCallback(() => {
		setStep('upload')
		setClientsFile(null)
		setPetsFile(null)
		setClientsParsed(null)
		setPetsParsed(null)
		setParseError('')
		setParsing(false)
		setProgress({ phase: '', current: 0, total: 0 })
		setImportResult(null)
		if (clientsInputRef.current) clientsInputRef.current.value = ''
		if (petsInputRef.current) petsInputRef.current.value = ''
	}, [])

	const handleClose = () => {
		// Block closing during importing to prevent partial state
		if (step === 'importing') return
		reset()
		onClose()
	}

	const handleClientsFileSelected = (e) => {
		const file = e.target.files?.[0]
		if (file) setClientsFile(file)
	}

	const handlePetsFileSelected = (e) => {
		const file = e.target.files?.[0]
		if (file) setPetsFile(file)
	}

	const handleNextFromUpload = async () => {
		if (!clientsFile) {
			setParseError('Please select a clients.csv file to import.')
			return
		}
		setParseError('')
		setParsing(true)

		try {
			const cParsed = await parseClientsCsv(clientsFile)
			if (cParsed.fatalError) {
				setParseError(cParsed.fatalError)
				setParsing(false)
				return
			}

			let pParsed = null
			if (petsFile) {
				const validOwnerIds = new Set(
					cParsed.rows.filter(r => r.ownerId).map(r => r.ownerId)
				)
				pParsed = await parsePetsCsv(petsFile, validOwnerIds)
				if (pParsed.fatalError) {
					setParseError(pParsed.fatalError)
					setParsing(false)
					return
				}
			}

			setClientsParsed(cParsed)
			setPetsParsed(pParsed)
			setStep('preview')
		} catch (e) {
			console.error('Parse error:', e)
			setParseError(e.message || 'Failed to parse CSV files.')
		} finally {
			setParsing(false)
		}
	}

	const handleConfirmImport = async () => {
		setStep('importing')
		setImportResult(null)

		const insertedClientIds = []
		const ownerIdToDbId = new Map()
		const insertedPetIds = []

		try {
			// Phase 1: Insert clients in chunks
			const allClientPayload = buildClientInsertRows(clientsParsed.rows, merchant.id)
			setProgress({ phase: 'Importing clients', current: 0, total: allClientPayload.length })

			for (let i = 0; i < allClientPayload.length; i += CLIENT_CHUNK_SIZE) {
				const chunkRows = clientsParsed.rows.slice(i, i + CLIENT_CHUNK_SIZE)
				const chunkPayload = allClientPayload.slice(i, i + CLIENT_CHUNK_SIZE)

				const { data, error } = await supabase
					.from('clients')
					.insert(chunkPayload)
					.select('id')

				if (error) {
					setImportResult({
						ok: false,
						phase: 'clients',
						message: error.message || 'Failed to insert clients.',
						insertedClientIds,
						insertedPetIds
					})
					setStep('result')
					return
				}

				data.forEach((row, j) => {
					const parsedRow = chunkRows[j]
					if (parsedRow.ownerId) ownerIdToDbId.set(parsedRow.ownerId, row.id)
					insertedClientIds.push(row.id)
				})

				setProgress({
					phase: 'Importing clients',
					current: Math.min(i + CLIENT_CHUNK_SIZE, allClientPayload.length),
					total: allClientPayload.length
				})
			}

			// Phase 2: Insert pets in chunks (if any)
			if (petsParsed && petsParsed.rows.length > 0) {
				let allPetPayload
				try {
					allPetPayload = buildPetInsertRows(petsParsed.rows, ownerIdToDbId)
				} catch (e) {
					setImportResult({
						ok: false,
						phase: 'pets',
						message: e.message,
						insertedClientIds,
						insertedPetIds
					})
					setStep('result')
					return
				}

				setProgress({ phase: 'Importing pets', current: 0, total: allPetPayload.length })

				for (let i = 0; i < allPetPayload.length; i += PET_CHUNK_SIZE) {
					const chunkPayload = allPetPayload.slice(i, i + PET_CHUNK_SIZE)

					const { data, error } = await supabase
						.from('pets')
						.insert(chunkPayload)
						.select('id')

					if (error) {
						setImportResult({
							ok: false,
							phase: 'pets',
							message: error.message || 'Failed to insert pets.',
							insertedClientIds,
							insertedPetIds
						})
						setStep('result')
						return
					}

					data.forEach(row => insertedPetIds.push(row.id))

					setProgress({
						phase: 'Importing pets',
						current: Math.min(i + PET_CHUNK_SIZE, allPetPayload.length),
						total: allPetPayload.length
					})
				}
			}

			if (isPostHogConfigured) {
				posthog.capture('clients_pets_imported', {
					client_count: insertedClientIds.length,
					pet_count: insertedPetIds.length,
				})
			}
			setImportResult({
				ok: true,
				insertedClients: insertedClientIds.length,
				insertedPets: insertedPetIds.length
			})
			setStep('result')
		} catch (e) {
			console.error('Import error:', e)
			setImportResult({
				ok: false,
				phase: 'unknown',
				message: e.message || 'An unexpected error occurred during import.',
				insertedClientIds,
				insertedPetIds
			})
			setStep('result')
		}
	}

	const handleRollback = async () => {
		if (!importResult || importResult.ok) return
		const { insertedClientIds, insertedPetIds } = importResult

		setStep('importing')
		setProgress({ phase: 'Rolling back', current: 0, total: insertedPetIds.length + insertedClientIds.length })

		try {
			// Delete pets first to satisfy any FK constraint
			for (let i = 0; i < insertedPetIds.length; i += PET_CHUNK_SIZE) {
				const chunk = insertedPetIds.slice(i, i + PET_CHUNK_SIZE)
				const { error } = await supabase.from('pets').delete().in('id', chunk)
				if (error) throw error
				setProgress(prev => ({
					...prev,
					current: Math.min(i + PET_CHUNK_SIZE, insertedPetIds.length)
				}))
			}

			for (let i = 0; i < insertedClientIds.length; i += CLIENT_CHUNK_SIZE) {
				const chunk = insertedClientIds.slice(i, i + CLIENT_CHUNK_SIZE)
				const { error } = await supabase.from('clients').delete().in('id', chunk)
				if (error) throw error
				setProgress(prev => ({
					...prev,
					current: insertedPetIds.length + Math.min(i + CLIENT_CHUNK_SIZE, insertedClientIds.length)
				}))
			}

			setImportResult({
				ok: false,
				phase: 'rolled_back',
				message: importResult.message,
				insertedClientIds: [],
				insertedPetIds: []
			})
		} catch (e) {
			console.error('Rollback error:', e)
			setImportResult({
				...importResult,
				rollbackError: e.message || 'Rollback failed; some records may remain.'
			})
		} finally {
			setStep('result')
		}
	}

	const handleFinish = () => {
		if (importResult?.ok || importResult?.phase === 'rolled_back') {
			if (onImportComplete) onImportComplete()
		} else if (importResult && importResult.insertedClientIds?.length > 0) {
			// User chose to keep partial data
			if (onImportComplete) onImportComplete()
		}
		reset()
		onClose()
	}

	// --- Render helpers ---

	const renderUploadStep = () => (
		<>
			<Typography sx={titleSx}>
				Import clients & pets
			</Typography>
			<Typography sx={subtitleSx}>
				Add new clients and pets in bulk from CSV files. Up to {MAX_ROWS_PER_FILE.toLocaleString()} rows per file.
			</Typography>

			{/* Templates */}
			<Box sx={sectionBoxSx}>
				<Typography sx={sectionTitleSx}>1. Download templates</Typography>
				<Typography sx={sectionDescSx}>
					Use these templates to format your data correctly. Edit them in Excel, Google Sheets, or Numbers.
				</Typography>
				<Box sx={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row', mt: '10px' }}>
					<TemplateLink
						href="/templates/clients-template.csv"
						filename="clients-template.csv"
						label="clients-template.csv"
					/>
					<TemplateLink
						href="/templates/pets-template.csv"
						filename="pets-template.csv"
						label="pets-template.csv"
					/>
				</Box>
			</Box>

			{/* Uploads */}
			<Box sx={sectionBoxSx}>
				<Typography sx={sectionTitleSx}>2. Upload your CSVs</Typography>
				<Typography sx={sectionDescSx}>
					<strong>clients.csv</strong> is required. <strong>pets.csv</strong> is optional &mdash; only if you want to import pets too. Pet rows link to clients via <code style={codeSx}>owner_id</code>.
				</Typography>

				<FileDropZone
					label="clients.csv (required)"
					file={clientsFile}
					onChange={handleClientsFileSelected}
					inputRef={clientsInputRef}
					required
				/>
				<FileDropZone
					label="pets.csv (optional)"
					file={petsFile}
					onChange={handlePetsFileSelected}
					inputRef={petsInputRef}
				/>
			</Box>

			{parseError && (
				<Alert severity="error" sx={{ mt: 1 }}>
					{parseError}
				</Alert>
			)}
		</>
	)

	const renderPreviewStep = () => {
		const cErrors = clientsParsed?.errors || []
		const cWarnings = clientsParsed?.warnings || []
		const pErrors = petsParsed?.errors || []
		const pWarnings = petsParsed?.warnings || []
		const totalErrors = cErrors.length + pErrors.length
		const totalWarnings = cWarnings.length + pWarnings.length

		const clientCount = clientsParsed?.rows.length || 0
		const petCount = petsParsed?.rows.length || 0

		const petsPerOwner = countPetsPerOwner(petsParsed?.rows || [])
		const ownersWithPets = petsPerOwner.size

		return (
			<>
				<Typography sx={titleSx}>Preview</Typography>
				<Typography sx={subtitleSx}>
					Review what will be imported. Fix any errors before continuing.
				</Typography>

				{/* Summary cards */}
				<Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
					<SummaryCard
						label="New clients"
						value={clientCount}
						color={COLORS.primaryText}
						bg={COLORS.primaryBg}
					/>
					{petsParsed && (
						<SummaryCard
							label="New pets"
							value={petCount}
							color={COLORS.primaryText}
							bg={COLORS.primaryBg}
							sublabel={`across ${ownersWithPets} owner${ownersWithPets === 1 ? '' : 's'}`}
						/>
					)}
					{totalErrors > 0 && (
						<SummaryCard
							label="Errors"
							value={totalErrors}
							color={COLORS.error}
							bg={COLORS.errorBg}
						/>
					)}
					{totalWarnings > 0 && (
						<SummaryCard
							label="Warnings"
							value={totalWarnings}
							color={COLORS.warning}
							bg={COLORS.warningBg}
						/>
					)}
				</Box>

				{/* Errors */}
				{totalErrors > 0 && (
					<Box sx={sectionBoxSx}>
						<Typography sx={{ ...sectionTitleSx, color: COLORS.error }}>
							Errors &mdash; must be fixed
						</Typography>
						<IssueList prefix="clients.csv" issues={cErrors} severity="error" />
						<IssueList prefix="pets.csv" issues={pErrors} severity="error" />
					</Box>
				)}

				{/* Warnings */}
				{totalWarnings > 0 && (
					<Box sx={sectionBoxSx}>
						<Typography sx={{ ...sectionTitleSx, color: COLORS.warning }}>
							Warnings &mdash; will still import
						</Typography>
						<IssueList prefix="clients.csv" issues={cWarnings} severity="warning" />
						<IssueList prefix="pets.csv" issues={pWarnings} severity="warning" />
					</Box>
				)}

				{/* Sample preview */}
				{totalErrors === 0 && clientCount > 0 && (
					<Box sx={sectionBoxSx}>
						<Typography sx={sectionTitleSx}>Sample preview</Typography>
						<Typography sx={sectionDescSx}>
							First few clients to be added:
						</Typography>
						<Box sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
							{clientsParsed.rows.slice(0, 5).map(r => {
								const pets = petsParsed?.rows.filter(p => p.ownerId === r.ownerId) || []
								return (
									<Box key={r.rowNum} sx={previewRowSx}>
										<Typography sx={previewNameSx}>
											{r.data.name}
											{r.data.last_name ? ` ${r.data.last_name}` : ''}
										</Typography>
										<Typography sx={previewMetaSx}>
											{r.data.email || r.data.phone_number || 'no contact info'}
											{pets.length > 0 && ` • ${pets.length} pet${pets.length === 1 ? '' : 's'}: ${pets.map(p => p.data.name).join(', ')}`}
										</Typography>
									</Box>
								)
							})}
							{clientsParsed.rows.length > 5 && (
								<Typography sx={{ ...previewMetaSx, fontStyle: 'italic' }}>
									...and {clientsParsed.rows.length - 5} more
								</Typography>
							)}
						</Box>
					</Box>
				)}
			</>
		)
	}

	const renderImportingStep = () => {
		const pct = progress.total > 0 ? (progress.current / progress.total) * 100 : 0
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', py: '40px' }}>
				<Typography sx={{ ...titleSx, textAlign: 'center' }}>
					{progress.phase || 'Working...'}
				</Typography>
				<Typography sx={{ ...subtitleSx, textAlign: 'center' }}>
					{progress.current.toLocaleString()} of {progress.total.toLocaleString()}
				</Typography>
				<Box sx={{ width: '100%', maxWidth: '400px' }}>
					<LinearProgress
						variant="determinate"
						value={pct}
						sx={{
							height: 8,
							borderRadius: 4,
							bgcolor: COLORS.grayBg,
							'& .MuiLinearProgress-bar': {
								bgcolor: COLORS.primaryBorder,
								borderRadius: 4
							}
						}}
					/>
				</Box>
				<Typography sx={{ fontSize: '12px', color: COLORS.textGray, textAlign: 'center' }}>
					Please don&apos;t close this window.
				</Typography>
			</Box>
		)
	}

	const renderResultStep = () => {
		if (!importResult) return null

		if (importResult.ok) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', py: '32px' }}>
					<CheckCircleIcon sx={{ fontSize: 64, color: COLORS.success }} />
					<Typography sx={{ ...titleSx, textAlign: 'center' }}>
						Import complete
					</Typography>
					<Typography sx={{ ...subtitleSx, textAlign: 'center' }}>
						Added <strong>{importResult.insertedClients}</strong> client{importResult.insertedClients === 1 ? '' : 's'}
						{importResult.insertedPets > 0 && (
							<> and <strong>{importResult.insertedPets}</strong> pet{importResult.insertedPets === 1 ? '' : 's'}</>
						)}.
					</Typography>
				</Box>
			)
		}

		if (importResult.phase === 'rolled_back') {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
					<Typography sx={titleSx}>Import rolled back</Typography>
					<Alert severity="info">
						All records added during this import have been deleted. The original error was: {importResult.message}
					</Alert>
				</Box>
			)
		}

		const { phase, message, insertedClientIds, insertedPetIds, rollbackError } = importResult
		const partialClients = insertedClientIds?.length || 0
		const partialPets = insertedPetIds?.length || 0
		const hasPartial = partialClients > 0 || partialPets > 0

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<Typography sx={{ ...titleSx, color: COLORS.error }}>
					Import failed
				</Typography>
				<Alert severity="error">
					{phase === 'clients' && 'Failed while importing clients. '}
					{phase === 'pets' && 'Failed while importing pets. '}
					{message}
				</Alert>
				{rollbackError && (
					<Alert severity="warning">{rollbackError}</Alert>
				)}
				{hasPartial && (
					<Box sx={sectionBoxSx}>
						<Typography sx={sectionTitleSx}>Partial data was added</Typography>
						<Typography sx={sectionDescSx}>
							{partialClients > 0 && (
								<>{partialClients} client{partialClients === 1 ? '' : 's'} {partialPets > 0 ? 'and ' : ''}</>
							)}
							{partialPets > 0 && (
								<>{partialPets} pet{partialPets === 1 ? '' : 's'} </>
							)}
							were added before the failure. You can keep them, or delete them and start over.
						</Typography>
					</Box>
				)}
			</Box>
		)
	}

	// --- Footer buttons per step ---

	const renderFooter = () => {
		if (step === 'upload') {
			return (
				<>
					<SecondaryButton onClick={handleClose} disabled={parsing}>Cancel</SecondaryButton>
					<PrimaryButton onClick={handleNextFromUpload} disabled={!clientsFile || parsing}>
						{parsing ? 'Reading...' : 'Next'}
					</PrimaryButton>
				</>
			)
		}
		if (step === 'preview') {
			const totalErrors = (clientsParsed?.errors.length || 0) + (petsParsed?.errors.length || 0)
			return (
				<>
					<SecondaryButton onClick={() => setStep('upload')}>Back</SecondaryButton>
					<PrimaryButton onClick={handleConfirmImport} disabled={totalErrors > 0}>
						{totalErrors > 0 ? `Fix ${totalErrors} error${totalErrors === 1 ? '' : 's'} first` : 'Confirm import'}
					</PrimaryButton>
				</>
			)
		}
		if (step === 'importing') {
			return null
		}
		if (step === 'result') {
			if (importResult?.ok || importResult?.phase === 'rolled_back') {
				return <PrimaryButton onClick={handleFinish}>Done</PrimaryButton>
			}
			const hasPartial = (importResult?.insertedClientIds?.length || 0) > 0
				|| (importResult?.insertedPetIds?.length || 0) > 0
			if (hasPartial) {
				return (
					<>
						<SecondaryButton onClick={handleRollback}>Delete added records</SecondaryButton>
						<PrimaryButton onClick={handleFinish}>Keep what was added</PrimaryButton>
					</>
				)
			}
			return <PrimaryButton onClick={handleFinish}>Close</PrimaryButton>
		}
		return null
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : '16px',
					bgcolor: COLORS.white
				}
			}}
		>
			{/* Header */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '16px 20px',
				borderBottom: `1px solid ${COLORS.grayBorder}`
			}}>
				<Typography sx={{
					fontFamily: FONT,
					fontWeight: 600,
					fontSize: '18px',
					color: COLORS.textDark
				}}>
					Import from CSV
				</Typography>
				<IconButton
					onClick={handleClose}
					disabled={step === 'importing'}
					size="small"
					sx={{ color: COLORS.textDark }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Content */}
			<Box sx={{
				padding: '20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '20px',
				overflowY: 'auto',
				flex: 1,
				minHeight: 0
			}}>
				{step === 'upload' && renderUploadStep()}
				{step === 'preview' && renderPreviewStep()}
				{step === 'importing' && renderImportingStep()}
				{step === 'result' && renderResultStep()}
			</Box>

			{/* Footer */}
			{renderFooter() && (
				<Box sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '10px',
					padding: '16px 20px',
					borderTop: `1px solid ${COLORS.grayBorder}`,
					flexWrap: 'wrap'
				}}>
					{renderFooter()}
				</Box>
			)}
		</Dialog>
	)
}

// --- Sub-components ---

function TemplateLink({ href, filename, label }) {
	return (
		<Button
			component="a"
			href={href}
			download={filename}
			startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px',
				padding: '10px 14px',
				bgcolor: COLORS.white,
				border: `1.5px solid ${COLORS.grayBorder}`,
				borderRadius: '10px',
				textTransform: 'none',
				fontFamily: FONT,
				fontWeight: 600,
				fontSize: '13.5px',
				color: COLORS.primaryText,
				'&:hover': {
					bgcolor: COLORS.white,
					borderColor: COLORS.primaryBorder
				}
			}}
		>
			{label}
		</Button>
	)
}

function FileDropZone({ label, file, onChange, inputRef, required = false }) {
	const handleClick = () => inputRef.current?.click()
	const handleClear = (e) => {
		e.stopPropagation()
		if (inputRef.current) inputRef.current.value = ''
		onChange({ target: { files: [] } })
	}

	return (
		<Box
			onClick={handleClick}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '12px',
				padding: '14px 16px',
				bgcolor: file ? COLORS.primaryBg : COLORS.white,
				border: `1.5px ${file ? 'solid' : 'dashed'} ${file ? COLORS.primaryBorder : COLORS.grayBorder}`,
				borderRadius: '10px',
				cursor: 'pointer',
				transition: 'background-color 0.15s, border-color 0.15s',
				mt: '8px',
				'&:hover': {
					bgcolor: file ? COLORS.primaryBg : COLORS.grayBg,
					borderColor: COLORS.primaryBorder
				}
			}}
		>
			<input
				ref={inputRef}
				type="file"
				accept=".csv,text/csv"
				onChange={onChange}
				style={{ display: 'none' }}
			/>
			{file ? (
				<DescriptionIcon sx={{ color: COLORS.primaryText, fontSize: 20 }} />
			) : (
				<UploadFileIcon sx={{ color: COLORS.textGray, fontSize: 20 }} />
			)}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography sx={{
					fontFamily: FONT,
					fontWeight: 500,
					fontSize: '14px',
					color: COLORS.textDark,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis'
				}}>
					{file ? file.name : label}
				</Typography>
				<Typography sx={{
					fontFamily: FONT,
					fontSize: '12px',
					color: COLORS.textGray
				}}>
					{file
						? `${(file.size / 1024).toFixed(1)} KB`
						: required ? 'Click to choose a file' : 'Click to choose a file (optional)'
					}
				</Typography>
			</Box>
			{file && (
				<Button
					onClick={handleClear}
					size="small"
					sx={{
						minWidth: 'auto',
						fontSize: '12px',
						textTransform: 'none',
						color: COLORS.primaryText
					}}
				>
					Change
				</Button>
			)}
		</Box>
	)
}

function SummaryCard({ label, value, color, bg, sublabel }) {
	return (
		<Box sx={{
			padding: '12px 16px',
			bgcolor: bg,
			borderRadius: '12px',
			minWidth: '120px',
			flex: '0 0 auto'
		}}>
			<Typography sx={{
				fontFamily: FONT,
				fontWeight: 700,
				fontSize: '20px',
				color
			}}>
				{value.toLocaleString()}
			</Typography>
			<Typography sx={{
				fontFamily: FONT,
				fontWeight: 500,
				fontSize: '12px',
				color
			}}>
				{label}
			</Typography>
			{sublabel && (
				<Typography sx={{
					fontFamily: FONT,
					fontSize: '11px',
					color,
					opacity: 0.8
				}}>
					{sublabel}
				</Typography>
			)}
		</Box>
	)
}

function IssueList({ prefix, issues, severity }) {
	if (!issues || issues.length === 0) return null
	const color = severity === 'error' ? COLORS.error : COLORS.warning

	const MAX_SHOWN = 10
	const shown = issues.slice(0, MAX_SHOWN)

	return (
		<Box sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
			{shown.map((issue, i) => (
				<Typography key={i} sx={{
					fontFamily: FONT,
					fontSize: '13px',
					color
				}}>
					<strong>{prefix}</strong>
					{issue.row ? ` row ${issue.row}: ` : ': '}
					{issue.message}
				</Typography>
			))}
			{issues.length > MAX_SHOWN && (
				<Typography sx={{
					fontFamily: FONT,
					fontSize: '12px',
					color,
					fontStyle: 'italic'
				}}>
					...and {issues.length - MAX_SHOWN} more
				</Typography>
			)}
		</Box>
	)
}

function PrimaryButton({ children, onClick, disabled }) {
	return (
		<Button
			onClick={onClick}
			disabled={disabled}
			disableElevation
			sx={{
				bgcolor: COLORS.primaryBorder,
				color: '#FFFFFF',
				border: 'none',
				borderRadius: '10px',
				padding: '11px 20px',
				textTransform: 'none',
				fontFamily: FONT,
				fontWeight: 700,
				fontSize: '13.5px',
				boxShadow: `0 2px 8px ${COLORS.primaryBg}`,
				'&:hover': {
					bgcolor: COLORS.primaryText,
					boxShadow: 'none'
				},
				'&:disabled': {
					bgcolor: COLORS.primaryBorder,
					color: '#FFFFFF',
					opacity: 0.5,
					boxShadow: 'none'
				}
			}}
		>
			{children}
		</Button>
	)
}

function SecondaryButton({ children, onClick, disabled }) {
	return (
		<Button
			onClick={onClick}
			disabled={disabled}
			sx={{
				bgcolor: COLORS.white,
				color: COLORS.textDark,
				border: `1.5px solid ${COLORS.grayBorder}`,
				borderRadius: '10px',
				padding: '11px 20px',
				textTransform: 'none',
				fontFamily: FONT,
				fontWeight: 650,
				fontSize: '13.5px',
				'&:hover': {
					bgcolor: COLORS.white,
					borderColor: COLORS.primaryBorder,
					color: COLORS.primaryText
				}
			}}
		>
			{children}
		</Button>
	)
}

// --- Shared inline styles ---

const titleSx = {
	fontFamily: FONT,
	fontWeight: 600,
	fontSize: '20px',
	color: COLORS.textDark
}

const subtitleSx = {
	fontFamily: FONT,
	fontSize: '14px',
	color: COLORS.textGray
}

const sectionBoxSx = {
	display: 'flex',
	flexDirection: 'column',
	gap: '6px',
	padding: '16px',
	bgcolor: COLORS.grayBg,
	border: `1px solid ${COLORS.grayBorder}`,
	borderRadius: '14px'
}

const sectionTitleSx = {
	fontFamily: FONT,
	fontWeight: 600,
	fontSize: '14px',
	color: COLORS.textDark
}

const sectionDescSx = {
	fontFamily: FONT,
	fontSize: '13px',
	color: COLORS.textGray
}

const codeSx = {
	background: '#EDF2F7',
	padding: '1px 6px',
	borderRadius: '4px',
	fontFamily: 'Menlo, monospace',
	fontSize: '12px'
}

const previewRowSx = {
	display: 'flex',
	flexDirection: 'column',
	gap: '2px',
	padding: '10px 12px',
	bgcolor: COLORS.white,
	border: `1px solid ${COLORS.grayBorder}`,
	borderRadius: '10px'
}

const previewNameSx = {
	fontFamily: FONT,
	fontWeight: 600,
	fontSize: '14px',
	color: COLORS.textDark
}

const previewMetaSx = {
	fontFamily: FONT,
	fontSize: '12px',
	color: COLORS.textGray
}

export default ImportClientsPetsDialog
